/**
 * usePackagesAnalytics.js
 *
 * Centralised analytics hook for the Packages (Pricing) section.
 * Mirrors the exact pattern of useBlogAnalytics.js / useRogyogAnalytics.js:
 *   - IS_PRODUCTION guard excludes localhost + 127.0.0.1
 *   - Dev mode: console.group + console.table with [PackagesAnalytics] prefix
 *   - react-ga4 used in production
 *
 * HOW TO USE:
 *   const analytics = usePackagesAnalytics();
 *   analytics.trackJoinClick(packageItem, classItem, isPrenatal);
 *
 * DEV MODE:
 *   Open browser DevTools → Console to see all events grouped under
 *   [PackagesAnalytics] in real time.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[PackagesAnalytics] ${eventName}`, "color:#E65C00;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const usePackagesAnalytics = () => {

  // ── Page view ────────────────────────────────────────────────────────────────

  const trackPageView = useCallback((title, url) => {
    send("packages_page_view", {
      page_title:    title,
      page_location: url,
    });
  }, []);

  // ── Card interactions ─────────────────────────────────────────────────────────

  /**
   * Fired when the user clicks "Join Now" on any pricing card
   * or clicks the card itself.
   *
   * @param {object} packageItem  - the parent package (name, id)
   * @param {object} classItem    - the clicked plan (className, classPrice, id)
   * @param {number} cardIndex    - 0-based position of the card in its grid
   * @param {boolean} isPrenatal  - whether this routes to /prenatalQuery
   * @param {boolean} isPopular   - whether this is the highlighted "Most Popular" card
   */
  const trackJoinClick = useCallback((packageItem, classItem, cardIndex, isPrenatal, isPopular) => {
    send("packages_join_click", {
      package_id:    String(packageItem?.id ?? ""),
      package_name:  packageItem?.name ?? "",
      plan_id:       String(classItem?.id ?? ""),
      plan_name:     classItem?.className ?? "",
      plan_price:    classItem?.classPrice ?? "",
      card_index:    cardIndex,
      is_popular:    isPopular,
      // destination helps distinguish prenatal vs generic enquiry funnel
      destination:   isPrenatal ? "prenatalQuery" : "contactUs",
    });
  }, []);

  // ── CTA banner ────────────────────────────────────────────────────────────────

  const trackCtaBannerClick = useCallback(() => {
    send("packages_cta_banner_click", {
      cta_label: "Book Online Yoga Class",
    });
  }, []);

  return {
    trackPageView,
    trackJoinClick,
    trackCtaBannerClick,
  };
};

export default usePackagesAnalytics;
